import * as THREE from 'three';
import { SolarSystem } from './scene/SolarSystem';
import { FlyControls } from './camera/FlyControls';
import { TeleportController } from './camera/Teleport';
import { PostFX } from './render/PostFX';
import { HUD } from './ui/HUD';
import { auToScene, radiusKmToScene } from './scale/units';

function dismissLoading(message?: string): void {
  const loading = document.getElementById('loading');
  const loadingText = document.getElementById('loading-text');
  if (message && loadingText) loadingText.textContent = message;
  if (loading) {
    loading.classList.add('hidden');
  }
}

async function bootstrap(): Promise<void> {
  const container = document.getElementById('canvas-container')!;
  const loadingFill = document.getElementById('loading-fill')!;
  const loadingText = document.getElementById('loading-text')!;

  const setProgress = (p: number, label: string) => {
    loadingFill.style.width = `${Math.round(p * 100)}%`;
    loadingText.textContent = label;
  };

  // Renderer
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    powerPreference: 'high-performance',
    alpha: false,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  container.appendChild(renderer.domElement);

  // Scene & camera
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x000005);
  scene.fog = new THREE.FogExp2(0x000005, 0.0000008);

  const camera = new THREE.PerspectiveCamera(
    55,
    window.innerWidth / window.innerHeight,
    0.01,
    1_000_000,
  );

  // Start near Earth — scale-aware approach position
  const earthAu = auToScene(1.0);
  const earthR = radiusKmToScene(6371);
  camera.position.set(earthAu + earthR * 12, earthR * 3, earthR * 8);

  // Solar system
  const solar = new SolarSystem();
  scene.add(solar.root);
  await solar.build(setProgress);

  // Place camera relative to actual Earth position after build
  const earth = solar.getBody('earth');
  if (earth) {
    const epos = earth.worldPosition;
    camera.position.set(
      epos.x + earth.radius * 14,
      epos.y + earth.radius * 4,
      epos.z + earth.radius * 10,
    );
    camera.lookAt(epos);
  }

  // Controls
  const controls = new FlyControls(camera, renderer.domElement);
  controls.setBodies(solar.getColliders());
  if (earth) {
    // Orient look toward Earth
    const m = new THREE.Matrix4().lookAt(
      camera.position,
      earth.worldPosition,
      new THREE.Vector3(0, 1, 0),
    );
    const q = new THREE.Quaternion().setFromRotationMatrix(m);
    controls.setLookFromQuaternion(q);
  }

  const teleport = new TeleportController(controls, camera);

  // Post-processing
  const post = new PostFX(renderer, scene, camera);

  // HUD
  const hud = new HUD(camera, (id) => {
    const body = solar.getBody(id);
    if (!body) return;
    teleport.goTo(body);
  });
  hud.ensureLabels(solar.bodies);

  // Resize
  window.addEventListener('resize', () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
    post.setSize(w, h);
  });

  // Hide loading — always, even if later frames fail
  setProgress(1, 'Enter the system');
  requestAnimationFrame(() => dismissLoading());

  // Dynamic near/far for precision near surfaces and outer system
  const clock = new THREE.Clock();

  function tick(): void {
    requestAnimationFrame(tick);
    const dt = Math.min(clock.getDelta(), 0.05);

    solar.update(dt);

    if (teleport.isActive) {
      teleport.update(dt);
    } else {
      controls.update(dt);
    }

    // Adaptive clipping planes based on nearest body
    const nearInfo = solar.findNearest(camera.position);
    if (nearInfo) {
      const r = nearInfo.body.radius;
      const d = camera.position.distanceTo(nearInfo.body.worldPosition);
      camera.near = THREE.MathUtils.clamp(d * 0.0002, 0.001, r * 0.05);
      camera.far = Math.max(800_000, d * 20);
      camera.updateProjectionMatrix();
    }

    // Prefer body under crosshair when looking at one
    const hover = hud.getHoverBody();
    let focusBody = nearInfo?.body ?? null;
    let focusDist = nearInfo?.dist ?? 0;
    if (hover) {
      focusBody = hover;
      focusDist = Math.max(
        0,
        camera.position.distanceTo(hover.worldPosition) - hover.radius,
      );
    }

    hud.update(
      solar.bodies,
      camera,
      controls.speedMagnitude || controls.speed * 0.15,
      focusBody,
      focusDist,
    );

    post.render();
  }

  tick();
}

bootstrap().catch((err) => {
  console.error(err);
  dismissLoading('Failed to initialize. Check console.');
});

// Absolute safety: never leave users on the splash forever
window.setTimeout(() => {
  const loading = document.getElementById('loading');
  if (loading && !loading.classList.contains('hidden')) {
    console.warn('[SkyTrek] Forced splash dismiss after timeout');
    dismissLoading('Taking longer than expected — entering anyway…');
  }
}, 12_000);
